// Estimated Shipping Date — fallback calculation for cart lines that don't
// already have an "Estimated Shipping Date" property.
//
// Backend contract (GET /apps/hub/esd/calculate?rush=&customColor=):
//   { date, display, customer_logged_in, customer_rushed }
// Shopify appends logged_in_customer_id to the proxy request automatically.
(function () {
  const ESD_LABEL = 'Estimated Shipping Date';
  const DEBUG = true;

  function log(...args) {
    if (DEBUG) console.log('[esd-cart]', ...args);
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async function fetchEsd(rush, customColor) {
    const params = new URLSearchParams({ rush, customColor });
    const url = `/apps/hub/esd/calculate?${params}`;

    try {
      const res = await fetch(url);
      if (!res.ok) return null;

      const data = await res.json();
      log(
        'customer_logged_in:', data.customer_logged_in,
        '| customer_rushed:', data.customer_rushed,
        '| date:', data.display,
      );
      return data;
    } catch (err) {
      console.error('[esd-cart] fetch error', err);
      return null;
    }
  }

  async function cartPropertiesByKey() {
    try {
      const res = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;

      const cart = await res.json();
      return Object.fromEntries((cart.items || []).map((item) => [item.key, item.properties || {}]));
    } catch (err) {
      console.error('[esd-cart] could not read cart', err);
      return null;
    }
  }

  function postChange(id, quantity, properties) {
    return fetch(`${cartRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity: Number(quantity), properties }),
    });
  }

  // Same configuration, ignoring what other scripts add to a line afterwards.
  function sameConfig(a, b) {
    const strip = (p) => {
      const o = Object.assign({}, p);
      delete o[ESD_LABEL];
      delete o._preview_cdn;
      return JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k]]));
    };
    return strip(a) === strip(b);
  }

  async function writeToCart(key, quantity, existingProps, display) {
    const res = await postChange(key, quantity, Object.assign({}, existingProps, { [ESD_LABEL]: display }));
    if (res.ok) return;

    // A line's key changes whenever ANY script rewrites its properties —
    // render-cdn-upgrade.js adds `_preview_cdn` seconds after an add — so the key
    // read at page load can be stale by the time we write ("no valid id or line
    // parameter", a 400). Find the line again by its configuration and write onto
    // its CURRENT properties, so what the other script added is not overwritten.
    try {
      const cart = await (await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const current = (cart.items || []).find((item) => item.key !== key
        && String(item.key).split(':')[0] === String(key).split(':')[0]
        && sameConfig(item.properties || {}, existingProps)
        && !(item.properties || {})[ESD_LABEL]);
      if (current) {
        await postChange(current.key, current.quantity, Object.assign({}, current.properties, { [ESD_LABEL]: display }));
      }
    } catch (err) {
      console.error('[esd-cart] retry after a stale line key failed', err);
    }
  }

  function logLineProps(props, rush, customColor) {
    log(
      'Rush Manufacturing:', JSON.stringify(props['Rush Manufacturing'] ?? '(not set)'),
      '| is_rushed:', rush,
    );
    const colorKey = Object.keys(props).find(k => k.toLowerCase().includes('color'));
    log(
      colorKey ? colorKey : 'Color',
      ':', JSON.stringify(colorKey ? props[colorKey] : '(not set)'),
      '| is_custom_color:', customColor,
    );
  }

  async function init() {
    const items = [...document.querySelectorAll('.estimate_shipping_date')]
      .filter(el => !el.dataset.esdExisting); // skip lines that already have a date

    if (!items.length) return;

    // Fire cart snapshot read and ALL ESD fetches simultaneously — no need to
    // wait for one before starting the others since they are independent.
    const [propsByKey, ...esdResults] = await Promise.all([
      cartPropertiesByKey(),
      ...items.map(el => fetchEsd(
        el.dataset.esdRush === 'true',
        el.dataset.esdCustomColor === 'true',
      )),
    ]);

    // Update DOM immediately so the user sees dates as fast as possible.
    // Then write to cart serially — /cart/change.js mutates one shared cart
    // object, so concurrent calls drop each other's writes.
    // The cart page renders every line more than once (desktop and mobile layouts),
    // so `items` holds several elements per line. Each LINE is written once: a write
    // changes the line's key, so a second write with the same key is a 400
    // ("no valid id or line parameter"). Every copy still gets the date on screen.
    const written = new Set();

    for (let i = 0; i < items.length; i++) {
      const el = items[i];
      const result = esdResults[i];
      const key = el.dataset.cartItemKey;
      const valueEl = el.querySelector('.estimate_date_update');

      if (!valueEl) continue;

      if (!result || !result.date) {
        valueEl.textContent = 'N/A';
        continue;
      }

      const display = result.display || result.date;

      // Show in DOM immediately — user sees the date without waiting for
      // the cart/change.js write to complete.
      valueEl.textContent = display;

      if (propsByKey && propsByKey[key] && !written.has(key)) {
        written.add(key);
        logLineProps(propsByKey[key], el.dataset.esdRush === 'true', el.dataset.esdCustomColor === 'true');
        // Write serially to avoid race condition on Shopify's cart object.
        await writeToCart(key, el.dataset.cartItemQuantity, propsByKey[key], display);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();